<template>
  <div>
    <div class="space-y-4">
      <!-- Page header -->
      <div class="flex items-center gap-3">
        <span class="flex items-center justify-center w-10 h-10 rounded-xl bg-taxiye-100 text-taxiye-500">
          <span class="material-icons-round">account_balance_wallet</span>
        </span>
        <div>
          <h2 class="text-lg font-semibold text-gray-800">Wallet Management</h2>
          <p class="text-sm text-gray-500">Review wallet transactions and deposits.</p>
        </div>
      </div>
      <!-- Filters -->
      <div class="p-4 bg-white border border-gray-200 rounded-2xl shadow-md">
        <div class="flex items-center justify-between mb-4">
          <div class="flex items-center space-x-1 text-gray-500">
            <span class="text-base material-icons-round">filter_list</span>
            <span class="text-xs font-semibold tracking-widest uppercase"
              >Filters</span
            >
          </div>
          <export-button
            :downloadUrl="reportDownloadUrl"
            :working="loading"
          ></export-button>
        </div>

        <div class="flex flex-wrap items-end gap-4">
          <!-- Driver -->
          <div class="relative w-full sm:w-56">
            <label
              class="block text-xs font-semibold tracking-wider text-gray-600 uppercase"
              >Driver</label
            >
            <div class="relative mt-1.5">
              <span
                class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none"
              >
                <span class="text-base text-gray-500 material-icons-round"
                  >search</span
                >
              </span>
              <input
                v-model="selectedDriver"
                @keyup="searchDriver"
                class="w-full h-10 pl-9 pr-3 text-sm text-gray-800 border-gray-300 shadow-sm form-input rounded-xl focus:border-taxiye-300"
                placeholder="Search drivers..."
              />
            </div>
            <transition
              enter-active-class="transition duration-100 ease-out"
              enter-class="transform scale-95 opacity-0"
              enter-to-class="transform scale-100 opacity-100"
              leave-active-class="transition duration-75 ease-in"
              leave-class="transform scale-100 opacity-100"
              leave-to-class="transform scale-95 opacity-0"
            >
              <div
                v-if="driverSearchResults.length"
                class="absolute inset-x-0 z-50 mt-2"
              >
                <div
                  class="py-1 overflow-y-auto bg-white border border-gray-100 shadow-lg rounded-xl max-h-64"
                >
                  <ul>
                    <li
                      v-for="(result, index) in driverSearchResults"
                      :key="result._id"
                      @click="selectDriver(index)"
                      class="flex items-center px-3 py-2 space-x-3 cursor-pointer transition-colors duration-150 hover:bg-taxiye-50"
                    >
                      <img
                        class="object-cover w-10 h-10 rounded-full"
                        :src="result.profileImage"
                        :alt="`${result.firstName} ${result.lastName}`"
                      />
                      <div>
                        <p class="text-sm font-semibold text-gray-800">
                          {{ `${result.firstName} ${result.lastName}` }}
                        </p>
                        <p class="text-xs text-gray-500">
                          {{ result.phoneNumber }}
                        </p>
                      </div>
                    </li>
                  </ul>
                </div>
              </div>
            </transition>
          </div>

          <!-- User -->
          <div class="relative w-full sm:w-56">
            <label
              class="block text-xs font-semibold tracking-wider text-gray-600 uppercase"
              >User</label
            >
            <div class="relative mt-1.5">
              <span
                class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none"
              >
                <span class="text-base text-gray-500 material-icons-round"
                  >search</span
                >
              </span>
              <input
                v-model="selectedAccount"
                @keyup="searchAccount"
                class="w-full h-10 pl-9 pr-3 text-sm text-gray-800 border-gray-300 shadow-sm form-input rounded-xl focus:border-taxiye-300"
                placeholder="Search user..."
              />
            </div>
            <transition
              enter-active-class="transition duration-100 ease-out"
              enter-class="transform scale-95 opacity-0"
              enter-to-class="transform scale-100 opacity-100"
              leave-active-class="transition duration-75 ease-in"
              leave-class="transform scale-100 opacity-100"
              leave-to-class="transform scale-95 opacity-0"
            >
              <div
                v-if="accountSearchResults.length"
                class="absolute inset-x-0 z-50 mt-2"
              >
                <div
                  class="py-1 overflow-y-auto bg-white border border-gray-100 shadow-lg rounded-xl max-h-64"
                >
                  <ul>
                    <li
                      v-for="(result, index) in accountSearchResults"
                      :key="result._id"
                      @click="selectAccount(index)"
                      class="px-3 py-2 cursor-pointer transition-colors duration-150 hover:bg-taxiye-50"
                    >
                      <p class="text-sm font-semibold text-gray-800">
                        {{ `${result.firstName} ${result.lastName}` }}
                      </p>
                    </li>
                  </ul>
                </div>
              </div>
            </transition>
          </div>

          <!-- By -->
          <div v-if="accountId == null" class="w-full sm:w-40">
            <label
              class="block text-xs font-semibold tracking-wider text-gray-600 uppercase"
              >By</label
            >
            <select
              class="w-full h-10 mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-select rounded-xl focus:border-taxiye-300"
              v-model="by"
              @change="getAllWallets"
            >
              <option value="all">All</option>
              <option value="admin">Admin</option>
              <option value="System">System</option>
            </select>
          </div>

          <!-- Date range -->
          <div class="w-full sm:w-auto">
            <label
              class="block text-xs font-semibold tracking-wider text-gray-600 uppercase"
              >Date Range</label
            >
            <div
              class="flex items-center h-10 mt-1.5 bg-white border border-gray-300 shadow-sm rounded-xl"
            >
              <span
                class="pl-3 pr-1 text-base text-gray-500 material-icons-round"
                >event</span
              >
              <v-date-picker v-model="range" is-range>
                <template v-slot="{ inputValue, inputEvents }">
                  <div class="flex items-center">
                    <input
                      :value="inputValue.start"
                      v-on="inputEvents.start"
                      placeholder="From"
                      class="w-24 h-8 px-2 text-sm text-gray-800 bg-transparent rounded-lg focus:outline-none focus:bg-taxiye-50"
                    />
                    <span
                      class="mx-1 text-base text-gray-300 material-icons-round"
                      >arrow_forward</span
                    >
                    <input
                      :value="inputValue.end"
                      v-on="inputEvents.end"
                      placeholder="To"
                      class="w-24 h-8 px-2 text-sm text-gray-800 bg-transparent rounded-lg focus:outline-none focus:bg-taxiye-50"
                    />
                  </div>
                </template>
              </v-date-picker>
              <button
                v-if="range.start || range.end"
                @click="range = { start: null, end: null }"
                type="button"
                title="Clear dates"
                class="flex items-center justify-center w-8 h-8 mx-1 text-gray-400 rounded-lg transition-colors duration-150 hover:bg-gray-100 hover:text-gray-600 focus:outline-none"
              >
                <span class="text-lg material-icons-round">close</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Selected driver / new transaction -->
      <transition name="fade">
        <div
          v-if="driverId"
          class="p-4 bg-white border border-gray-200 rounded-2xl shadow-md"
        >
          <div class="flex flex-wrap items-center justify-between gap-4">
            <div class="flex items-center space-x-1 text-gray-500">
              <span class="text-base material-icons-round"
                >account_balance_wallet</span
              >
              <span class="text-xs font-semibold tracking-widest uppercase"
                >Driver Wallet</span
              >
            </div>
            <div class="flex flex-wrap items-center gap-3">
              <div
                class="px-4 py-2 border border-gray-200 rounded-xl bg-gray-50"
              >
                <p
                  class="text-xs font-semibold tracking-wider text-gray-500 uppercase"
                >
                  Selected Driver
                </p>
                <p class="text-sm font-semibold text-gray-900">
                  {{ driverName }}
                </p>
              </div>
              <div
                class="px-4 py-2 border rounded-xl border-taxiye-200 bg-taxiye-50"
              >
                <p
                  class="text-xs font-semibold tracking-wider text-taxiye-600 uppercase"
                >
                  Balance
                </p>
                <p class="text-sm font-semibold text-gray-900">
                  {{ driverBalance }}
                </p>
              </div>
            </div>
          </div>

          <div v-if="adding" class="mt-4 space-y-4">
            <div class="flex flex-wrap items-end gap-4">
              <!-- Payment type -->
              <div class="w-full sm:w-48">
                <label
                  class="block text-xs font-semibold tracking-wider text-gray-600 uppercase"
                  >Payment Type</label
                >
                <select
                  class="w-full h-10 mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-select rounded-xl focus:border-taxiye-300"
                  v-model="paymentType"
                >
                  <option value="cash">Cash</option>
                  <option value="bank_deposit">Bank Deposit</option>
                </select>
              </div>
              <!-- Amount -->
              <div class="w-full sm:w-56">
                <label
                  class="block text-xs font-semibold tracking-wider text-gray-600 uppercase"
                  >Amount</label
                >
                <div class="relative mt-1.5">
                  <span
                    class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none"
                  >
                    <span class="text-base text-gray-500 material-icons-round"
                      >payments</span
                    >
                  </span>
                  <input
                    v-model="amount"
                    type="number"
                    ref="amountInput"
                    class="w-full h-10 pl-9 pr-3 text-sm text-gray-800 shadow-sm form-input rounded-xl focus:border-taxiye-300"
                    :class="
                      amountError
                        ? 'border-red-500 bg-red-50'
                        : 'border-gray-300'
                    "
                    placeholder="Enter the amount"
                  />
                </div>
              </div>
              <!-- Reason -->
              <div class="w-full sm:w-56">
                <label
                  class="block text-xs font-semibold tracking-wider text-gray-600 uppercase"
                  >Reason</label
                >
                <input
                  v-model="reason"
                  type="text"
                  class="w-full h-10 px-3 mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-input rounded-xl focus:border-taxiye-300"
                  placeholder="Enter reason"
                />
              </div>
            </div>

            <!-- Bank deposit details -->
            <div
              v-if="paymentType === 'bank_deposit'"
              class="flex flex-wrap items-end gap-4"
            >
              <div class="w-full sm:w-56">
                <label
                  class="block text-xs font-semibold tracking-wider text-gray-600 uppercase"
                  >Bank</label
                >
                <select
                  class="w-full h-10 mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-select rounded-xl focus:border-taxiye-300"
                  v-model="deposit.bank"
                >
                  <option value="" disabled hidden>Select bank</option>
                  <option value="awash">Awash International Bank</option>
                  <option value="abyssinia">Bank of Abyssinia</option>
                  <option value="berhan">
                    Berhan International Bank S.C.
                  </option>
                  <option value="cbe">
                    Commercial Bank of Ethiopia (CBE)
                  </option>
                  <option value="dashen">Dashen Bank</option>
                  <option value="enat">Enat Bank</option>
                  <option value="lion">Lion International Bank S.C</option>
                  <option value="NBE">
                    National Bank of Ethiopia (NBE) (Central Bank)
                  </option>
                  <option value="nib">Nib International Bank</option>
                  <option value="united">United Bank</option>
                  <option value="wegagen">Wegagen Bank</option>
                  <option value="zemen">Zemen Bank S.C</option>
                  <option value="coop">
                    Cooperative Bank of Oromia(s.c.)
                  </option>
                  <option value="debub">Debub Global Bank</option>
                  <option value="bunna">Bunna International Bank</option>
                </select>
              </div>
              <div class="w-full sm:w-48">
                <label
                  class="block text-xs font-semibold tracking-wider text-gray-600 uppercase"
                  >Deposited By</label
                >
                <input
                  v-model="deposit.by"
                  type="text"
                  class="w-full h-10 px-3 mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-input rounded-xl focus:border-taxiye-300"
                  placeholder="Deposited by"
                />
              </div>
              <div class="w-full sm:w-48">
                <label
                  class="block text-xs font-semibold tracking-wider text-gray-600 uppercase"
                  >Transaction #</label
                >
                <input
                  v-model="deposit.transaction"
                  type="text"
                  class="w-full h-10 px-3 mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-input rounded-xl focus:border-taxiye-300"
                  placeholder="Transaction"
                />
              </div>
              <div class="w-full sm:w-48">
                <label
                  class="block text-xs font-semibold tracking-wider text-gray-600 uppercase"
                  >Narrative</label
                >
                <input
                  v-model="deposit.narrative"
                  type="text"
                  class="w-full h-10 px-3 mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-input rounded-xl focus:border-taxiye-300"
                  placeholder="Narrative"
                />
              </div>
              <div class="w-full sm:w-44">
                <label
                  class="block text-xs font-semibold tracking-wider text-gray-600 uppercase"
                  >Deposit Date</label
                >
                <v-date-picker
                  v-model="deposit.date"
                  :popover="{ visibility: 'click' }"
                >
                  <template v-slot="{ inputValue, inputEvents }">
                    <input
                      :value="inputValue"
                      v-on="inputEvents"
                      placeholder="Select date"
                      class="w-full h-10 px-3 mt-1.5 text-sm text-gray-800 border border-gray-300 shadow-sm rounded-xl focus:outline-none focus:border-taxiye-300"
                    />
                  </template>
                </v-date-picker>
              </div>
            </div>
          </div>

          <div class="flex items-center gap-3 mt-4">
            <button
              :disabled="busy || invalidTransaction"
              @click="handleClick"
              class="inline-flex items-center h-10 px-4 text-sm font-semibold text-white rounded-xl shadow-sm transition-colors duration-150"
              :class="{
                'bg-taxiye-300 cursor-not-allowed': busy,
                'bg-taxiye-500 hover:bg-taxiye-600 focus:bg-taxiye-600':
                  !busy && !invalidTransaction,
                'bg-taxiye-200 text-gray-500 cursor-not-allowed': invalidTransaction,
              }"
            >
              <template v-if="adding">
                <span class="mr-1 text-base material-icons-round"
                  >account_balance_wallet</span
                >
                <span>Add Transaction</span>
              </template>
              <template v-else>
                <span class="mr-1 text-base material-icons-round">add</span>
                <span>New Transaction</span>
              </template>
            </button>
            <button
              v-if="adding"
              @click="adding = false"
              type="button"
              class="inline-flex items-center h-10 px-4 text-sm font-semibold text-gray-600 bg-white border border-gray-300 rounded-xl shadow-sm transition-colors duration-150 hover:bg-gray-50 focus:outline-none"
            >
              Cancel
            </button>
          </div>
        </div>
      </transition>

      <!-- Wallet history -->
      <div
        class="overflow-hidden bg-white border border-gray-200 rounded-xl shadow-md"
      >
        <div
          class="flex items-center px-5 py-4 space-x-1 text-gray-500 border-b border-gray-200"
        >
          <span class="text-base material-icons-round">receipt_long</span>
          <span class="text-xs font-semibold tracking-widest uppercase"
            >Wallet History / Transactions</span
          >
        </div>
        <div class="overflow-x-auto">
          <table class="min-w-full text-sm">
            <thead>
              <tr class="bg-gray-100 border-b border-gray-200">
                <th
                  v-for="col in [
                    'Driver',
                    'Amount',
                    'Payment Type',
                    'By',
                    'User',
                    'Reason',
                    'Date',
                    'Status',
                    'Balance (Then)',
                    'Receipt',
                    'Trip',
                  ]"
                  :key="`col-${col}`"
                  class="px-5 py-3 text-xs font-semibold tracking-wider text-left text-gray-500 uppercase whitespace-no-wrap"
                >
                  {{ col }}
                </th>
              </tr>
            </thead>
            <tbody v-if="wallets.length" class="relative">
              <tr
                v-for="(wallet, index) in wallets"
                :key="wallet._id"
                class="transition-colors duration-150 hover:bg-taxiye-50"
                :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-100'"
              >
                <!-- Driver -->
                <td class="px-5 py-4 whitespace-no-wrap">
                  <div v-if="wallet.driver" class="flex items-center space-x-3">
                    <span
                      class="flex items-center justify-center flex-shrink-0 w-10 h-10 text-xs font-bold rounded-full"
                      :class="avatarClass(personLabel(wallet.driver))"
                      >{{ initials(personLabel(wallet.driver)) }}</span
                    >
                    <p class="font-medium text-gray-900">
                      {{ personLabel(wallet.driver, "Unknown") }}
                    </p>
                  </div>
                  <span v-else class="text-gray-400">—</span>
                </td>
                <!-- Amount -->
                <td class="px-5 py-4 whitespace-no-wrap">
                  <span class="font-semibold text-gray-900">
                    {{ wallet.amount.toFixed(2) }}
                  </span>
                </td>
                <!-- Payment type -->
                <td
                  class="px-5 py-4 text-gray-600 capitalize whitespace-no-wrap"
                >
                  {{
                    wallet.paymentType
                      ? wallet.paymentType.replace(/_/g, " ")
                      : "—"
                  }}
                </td>
                <!-- By -->
                <td
                  class="px-5 py-4 text-gray-600 capitalize whitespace-no-wrap"
                >
                  {{ wallet.by ? wallet.by : "—" }}
                </td>
                <!-- User -->
                <td class="px-5 py-4 text-gray-600 whitespace-no-wrap">
                  {{
                    wallet.account
                      ? personLabel(wallet.account, "—")
                      : wallet.from
                      ? personLabel(wallet.from, "—")
                      : "—"
                  }}
                </td>
                <!-- Reason -->
                <td class="px-5 py-4 text-gray-600">
                  <p class="truncate max-w-xs" :title="wallet.reason">
                    {{ wallet.reason ? wallet.reason : "—" }}
                  </p>
                </td>
                <!-- Date -->
                <td class="px-5 py-4 text-gray-600 whitespace-no-wrap">
                  <div class="flex items-center space-x-2">
                    <i class="text-sm text-gray-400 material-icons-round"
                      >schedule</i
                    >
                    <span>{{ formatDate(wallet.updatedAt) }}</span>
                  </div>
                </td>
                <!-- Status -->
                <td class="px-5 py-4 whitespace-no-wrap">
                  <span
                    class="inline-flex items-center px-2 py-1 text-xs font-semibold leading-none uppercase rounded-full"
                    :class="
                      wallet.status === 'paid'
                        ? 'text-green-700 bg-green-100'
                        : wallet.status === 'unpaid'
                        ? 'text-blue-700 bg-blue-100'
                        : 'text-gray-600 bg-gray-100'
                    "
                  >
                    <span
                      class="w-2 h-2 mr-2 rounded-full"
                      :class="
                        wallet.status === 'paid'
                          ? 'bg-green-500'
                          : wallet.status === 'unpaid'
                          ? 'bg-blue-500'
                          : 'bg-gray-400'
                      "
                    ></span>
                    {{ wallet.status ? wallet.status : "—" }}
                  </span>
                </td>
                <!-- Balance (then) -->
                <td
                  class="px-5 py-4 whitespace-no-wrap"
                  :class="
                    isNaN(wallet.currentAmount)
                      ? 'text-gray-400'
                      : 'font-medium text-gray-900'
                  "
                >
                  {{
                    wallet.currentAmount || wallet.currentAmount == 0
                      ? wallet.currentAmount.toFixed(2)
                      : "NO RECORD"
                  }}
                </td>
                <!-- Bank receipt -->
                <td class="px-5 py-4 whitespace-no-wrap">
                  <router-link
                    v-if="wallet.paymentType === 'bank_deposit'"
                    :to="'/wallet-managment/' + wallet._id + '/show'"
                    title="View bank receipt"
                    class="inline-flex items-center justify-center w-9 h-9 text-gray-500 rounded-lg transition-colors duration-150 hover:bg-gray-100 hover:text-gray-700"
                  >
                    <i class="material-icons-round">account_balance</i>
                  </router-link>
                  <span v-else class="text-gray-300">—</span>
                </td>
                <!-- Trip -->
                <td class="px-5 py-4 whitespace-no-wrap">
                  <router-link
                    v-if="wallet.ride"
                    :to="'/rides/' + wallet.ride._id + '/show'"
                    title="View trip"
                    class="inline-flex items-center justify-center w-9 h-9 text-gray-500 rounded-lg transition-colors duration-150 hover:bg-gray-100 hover:text-gray-700"
                  >
                    <i class="material-icons-round">visibility</i>
                  </router-link>
                  <span v-else class="text-gray-300">—</span>
                </td>
              </tr>
              <div
                v-if="loading"
                class="absolute inset-0 flex items-center justify-center w-full h-full bg-white bg-opacity-50"
              >
                <i class="text-5xl text-taxiye-500 material-icons-round spin"
                  >sync</i
                >
                <span class="invisible block">Loading...</span>
              </div>
            </tbody>
            <tbody v-else>
              <tr>
                <td colspan="11" class="py-16 text-center bg-white">
                  <div v-if="loading" class="inline-block">
                    <i
                      class="text-5xl text-taxiye-500 material-icons-round spin"
                      >sync</i
                    >
                    <span class="invisible block">Loading...</span>
                  </div>
                  <div v-else class="flex flex-col items-center text-gray-400">
                    <span
                      class="flex items-center justify-center w-16 h-16 mb-3 rounded-full bg-gray-100"
                    >
                      <i class="text-3xl material-icons-round"
                        >account_balance_wallet</i
                      >
                    </span>
                    <span class="text-sm font-medium text-gray-500"
                      >No wallet transactions found</span
                    >
                    <span class="mt-1 text-xs text-gray-400"
                      >Try adjusting your filters</span
                    >
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="flex items-end justify-between">
        <pagination
          class="flex space-x-2"
          :currentPage="currentPage"
          :total="total"
          @goto="goToPage"
          @setPerPage="setPerPage"
          :perPage="perPage"
          :start="start"
          :end="end"
          :count="paginator.count"
        ></pagination>
      </div>
    </div>
  </div>
</template>

<script>
import PaginationMixin from "@/mixins/Pagination";
import ExportButton from "@/components/ExportButton";
import Pagination from "@/components/Pagination";

export default {
  mixins: [PaginationMixin],
  data() {
    return {
      loading: false,
      wallets: [],
      selectedDriver: "",
      drivers: [],
      driverSearchResults: [],
      driverId: null,
      by: "all",
      accountId: null,
      paymentType: "cash",
      accounts: [],
      adding: false,
      amount: 10,
      accountSearchResults: [],
      selectedAccount: "",
      reason: "Topup",
      amountError: false,
      busy: false,
      selectedDriverObj: null,
      range: {
        start: null,
        end: null,
      },
      deposit: {
        bank: "",
        by: "",
        transaction: "",
        narrative: "",
        date: "",
      },
      driverResultIndex: null,
    };
  },
  components: {
    ExportButton,
    Pagination,
  },
  watch: {
    amount() {
      this.amountError = false;
    },
    range: {
      handler() {
        this.getAllWallets();
      },
      deep: true,
    },
    currentPage() {
      this.getAllWallets();
    },
    perPage() {
      this.getAllWallets();
    },
    $route() {
      if (this.$route.query.driver) {
        this.driverId = this.$route.query.driver;
        this.$misc
          .get(`/drivers/${this.driverId}`)
          .then(({ data: { driver } }) => {
            this.selectedDriverObj = driver;
          })
          .catch(() => {
            window.flash({
              type: "error",
              body: "Failed to fetch the new balance! please refresh",
            });
          });
      }
    },
  },
  computed: {
    role() {
      return this.$store.state.role;
    },
    driverBalance() {
      if (this.selectedDriverObj)
        return this.selectedDriverObj.ballance.toFixed(2);
      else return " N/A ";
    },
    driverName() {
      if (this.selectedDriverObj)
        return [
          this.selectedDriverObj.firstName,
          this.selectedDriverObj.lastName,
        ].join(" ");
      else return " N/A ";
    },
    invalidTransaction() {
      if (this.paymentType === "bank_deposit") {
        if (
          this.deposit.by &&
          this.deposit.bank &&
          this.deposit.transaction &&
          this.deposit.narrative &&
          this.deposit.date
        ) {
          return false;
        } else {
          return true;
        }
      } else {
        return false;
      }
    },
    reportDownloadUrl() {
      return (
        "wallet-histories/export?" +
        Object.entries({
          ...Object.fromEntries(
            Object.entries({
              driver: this.driverId ? this.driverId : undefined,
              account: this.accountId ? this.accountId : undefined,
              by: this.by && this.by != "all" ? this.by : undefined,
              ...(this.range || {}),
            }).filter(([key, val]) => key && val)
          ),
        })
          .map(([key, value]) => [key, value].join("="))
          .join("&")
      );
    },
  },
  created() {
    var sd = new Date();
    sd.setDate(0);
    sd.setDate(1);
    var ed = new Date();
    ed.setMonth(ed.getMonth() + 1);
    ed.setDate(0);
    this.range.start = sd;
    this.range.end = ed;
    if (this.$route.query.driver) {
      this.driverId = this.$route.query.driver;
      this.adding = true;
      this.$misc
        .get(`/drivers/${this.driverId}`)
        .then(({ data: { driver } }) => {
          this.selectedDriverObj = driver;
        })
        .catch(() => {
          window.flash({
            type: "error",
            body: "Failed to fetch the new balance! please refresh",
          });
        });
    } else {
      this.getAllWallets();
    }
    this.$misc
      .get("/drivers")
      .then(response => {
        this.drivers = response.data.data;
        let driver = this.drivers.find(driver => driver._id == this.driverId);
        if (driver) {
          this.selectedDriver = `${driver.firstName} ${driver.lastName} - ${driver.phoneNumber}`;
        }
      })
      .catch(err => console.log(err));

    this.$misc
      .get("/accounts")
      .then(response => (this.accounts = response.data.data))
      .catch(err => console.log(err));
  },
  methods: {
    handleClick() {
      this.adding ? this.add() : this.openAdd();
    },
    openAdd() {
      this.adding = true;
      this.$nextTick(() => {
        this.$refs.amountInput.focus();
      });
    },
    formatDate(date) {
      return date ? date.replace("T", " ").slice(0, 16) : "";
    },
    personLabel(person, fallback = "") {
      if (!person) return fallback;
      const name = `${person.firstName || ""} ${person.lastName || ""}`.trim();
      if (!name) return fallback;
      return name
        .split(" ")
        .filter(Boolean)
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(" ");
    },
    initials(name) {
      if (!name) return "?";
      return name
        .split(" ")
        .filter(Boolean)
        .map(part => part[0])
        .slice(0, 2)
        .join("")
        .toUpperCase();
    },
    avatarClass(name) {
      const palette = [
        "bg-taxiye-100 text-taxiye-600",
        "bg-indigo-50 text-indigo-600",
        "bg-blue-50 text-blue-600",
        "bg-green-50 text-green-600",
        "bg-purple-50 text-purple-600",
        "bg-pink-50 text-pink-600",
      ];
      let hash = 0;
      for (let i = 0; i < (name || "").length; i++) {
        hash = (hash + name.charCodeAt(i)) % palette.length;
      }
      return palette[hash];
    },
    getAllWallets() {
      this.loading = true;
      var path = `/wallet-histories?populate=["driver","account","from", "ride"]&page=${this.currentPage}&limit=${this.perPage}`;
      if (this.driverId) {
        path += "&driver=" + this.driverId;
      }
      if (this.accountId) {
        path += "&account=" + this.accountId;
      }
      if (this.by && this.by != "all") {
        path += "&by=" + this.by;
      }
      if (this.range.start) {
        path += "&start=" + this.range.start;
      }
      if (this.range.end) {
        path += "&end=" + this.range.end;
      }
      return this.$misc
        .get(path)
        .then(response => {
          this.wallets = response.data.data;
          this.paginator = {
            nextPage: response.data.nextPage,
            prevPage: response.data.prevPage,
            count: response.data.count,
          };
        })
        .catch(err => console.log(err))
        .finally(() => (this.loading = false));
    },
    searchDriver(e) {
      if (e.target.value.trim() === "") {
        this.driverSearchResults = [];
        this.driverId = null;
        this.getAllWallets();
      } else {
        const name = e.target.value.trim();

        if (name) {
          this.driverSearchResults = [];
          this.$misc
            .get("drivers/search?vehicle=true&limit=5&q=" + name)
            .then(({ data }) => {
              this.driverSearchResults = data;
            })
            .catch(err => console.log(err))
            .finally(() => (this.loading = false));
        } else {
          this.drivers = [];
        }
      }
    },
    searchAccount(e) {
      if (e.target.value.trim() === "") {
        this.accountSearchResults = [];
        this.accountId = null;
        this.getAllWallets();
      } else if (!isNaN(parseInt(e.target.value))) {
        this.accountSearchResults = this.accounts.filter(account => {
          return account.phoneNumber.slice(-4).includes(e.target.value);
        });
      } else {
        this.accountSearchResults = this.accounts.filter(account => {
          let name = account.firstName + " " + account.lastName;
          return name.toLowerCase().includes(e.target.value.toLowerCase());
        });
      }
    },
    selectDriver(index) {
      let driver = this.driverSearchResults[index];
      this.selectedDriverObj = driver;
      this.selectedDriver = `${driver.firstName} ${driver.lastName} - ${driver.phoneNumber}`;
      this.driverId = driver._id;
      this.driverSearchResults = [];
      this.getAllWallets();
    },
    selectAccount(index) {
      let account = this.accountSearchResults[index];
      this.selectedAccount = `${account.firstName} ${account.lastName}`;
      this.accountId = account._id;
      this.accountSearchResults = [];
      this.by = "all";
      this.getAllWallets();
    },
    add() {
      this.busy = true;
      const amount = parseInt(this.amount);
      if (isNaN(amount)) {
        this.amountError = true;
        this.busy = false;
        return false;
      }
      this.$misc
        .post(`/drivers/${this.driverId}/top-up`, {
          amount,
          account: this.$store.state.auth.account._id,
          reason: this.reason ? this.reason : "Topup",
          paymentType: this.paymentType,
          deposit:
            this.paymentType === "bank_deposit" ? this.deposit : undefined,
        })
        .then(() => {
          this.getAllWallets();
          this.$misc
            .get(`/drivers/${this.driverId}`)
            .then(({ data: { driver } }) => {
              this.selectedDriverObj = driver;
            })
            .catch(() => {
              window.flash({
                type: "error",
                body: "Failed to fetch the new balance! please refresh",
              });
            });
          window.flash({
            type: "success",
            body: "Wallet added successfully to driver!",
          });
          this.adding = false;
          this.amount = 0;
        })
        .catch(err => {
          console.log(err);
          window.flash({
            type: "error",
            body: "Failed to add wallet!",
          });
        })
        .finally(() => {
          this.busy = false;
        });
    },
  },
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s;
}
.fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */ {
  opacity: 0;
}
</style>
